import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Popover,
  Radio,
  RadioGroup,
  TableContainer,
} from '@material-ui/core'
import EnhancedTable from 'components/tables/enhanced-table'
import SearchBar from 'pages/pga-coach/students/search-bar'
import StudentsTablePagination from 'pages/pga-coach/students/students-table-pagination'
import SectionEmptyState from 'components/section-empty-state'
import PermContactCalendarOutlinedIcon from 'components/material-symbol/icons/PermContactCalendar'
import ArrowDropDownIcon from 'components/material-symbol/icons/ArrowDropDown'
import SwapVertIcon from 'components/material-symbol/icons/SwapVert'
import { useIsTablet } from 'lib/hooks'
import { sortByAgeGroup } from 'lib/student-list/utils'
import hasNonNullProperty from 'lib/utils/has-non-null-property'
import { useStyles } from './styles'
import AccountStudentRow from './account-student-row'
import { isInAgeGroup, matchesSearchTerm } from './accounts-table-helpers'

const AGE_FILTER_OPTIONS = [
  { value: 'all', label: 'All Ages' },
  { value: 'adult', label: 'Adult' },
  { value: 'junior', label: 'Junior' },
]

const sortByFullName = (a, b, dir) => {
  const getName = row =>
    `${row.props.student.firstName}${row.props.student.lastName}`
      .toLowerCase()
      .replace(/\s/g, '')
  const left = getName(a)
  const right = getName(b)

  if (left > right) return dir === 'desc' ? -1 : 1
  if (left < right) return dir === 'desc' ? 1 : -1
  return 0
}

const sortByProfileStatus = (a, b, dir) => {
  const profileRank = student =>
    hasNonNullProperty(student?.golferProfile) ? 1 : 0
  const left = profileRank(a.props.student)
  const right = profileRank(b.props.student)

  if (left < right) return dir === 'desc' ? 1 : -1
  if (left > right) return dir === 'desc' ? -1 : 1
  return 0
}

const AccountsTable = ({ representatives = [] }) => {
  const classes = useStyles()
  const isTablet = useIsTablet()
  const [searchTerm, setSearchTerm] = useState('')
  const [ageGroup, setAgeGroup] = useState('all')
  const [filterAnchorEl, setFilterAnchorEl] = useState(null)
  const [page, setPage] = useState(0)
  const rowsPerPage = 25

  useLayoutEffect(() => {
    window.sessionStorage.setItem('AccountsSortColumn', 'name')
    window.sessionStorage.setItem('AccountsSortDirection', 'asc')
  }, [])

  const filteredRepresentatives = useMemo(() => {
    return representatives.filter(rep => {
      return (
        matchesSearchTerm(rep.student, searchTerm) &&
        isInAgeGroup(rep.student, ageGroup)
      )
    })
  }, [representatives, searchTerm, ageGroup])

  useEffect(() => {
    setPage(0)
  }, [searchTerm, ageGroup, representatives])

  const filterMenuOpen = Boolean(filterAnchorEl)
  const hasActiveAgeFilter = ageGroup !== 'all'
  const activeAgeGroupLabel =
    AGE_FILTER_OPTIONS.find(option => option.value === ageGroup)?.label ||
    'Filters'

  const filterButtons = (
    <>
      <Button
        variant="outlined"
        color="default"
        className={`${classes.filterButton} ${
          hasActiveAgeFilter ? classes.filterButtonActive : ''
        }`}
        aria-controls="account-filters-menu"
        aria-haspopup="true"
        aria-expanded={filterMenuOpen}
        aria-label={
          hasActiveAgeFilter
            ? `Account age group filter: ${activeAgeGroupLabel} selected`
            : 'Account age group filter'
        }
        onClick={event => setFilterAnchorEl(event.currentTarget)}
        endIcon={<ArrowDropDownIcon />}
      >
        {hasActiveAgeFilter ? activeAgeGroupLabel : 'Filters'}
      </Button>
      <Popover
        id="account-filters-menu"
        open={filterMenuOpen}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          className: classes.filterMenuPaper,
          style: filterAnchorEl
            ? { minWidth: filterAnchorEl.clientWidth }
            : undefined,
        }}
      >
        <Box className={classes.filterMenuContent}>
          <FormControl
            component="fieldset"
            fullWidth
            className={classes.filterForm}
          >
            <FormLabel component="legend" className={classes.filterLegend}>
              Age Group
            </FormLabel>
            <RadioGroup
              aria-label="Age group filter"
              name="age-group-filter"
              className={classes.filterRadioGroup}
              value={ageGroup}
              onChange={event => {
                setAgeGroup(event.target.value)
                setFilterAnchorEl(null)
              }}
            >
              {AGE_FILTER_OPTIONS.map(option => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  className={classes.filterRadioLabel}
                  control={<Radio color="primary" size="small" />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Box>
      </Popover>
    </>
  )

  const columns = [
    {
      id: 'name',
      label: 'Name',
      align: 'left',
      width: isTablet ? '45%' : '24%',
      mobile: true,
      sortable: true,
      sortComparator: sortByFullName,
    },
    {
      id: 'ageGroup',
      label: 'Age Group',
      align: 'left',
      width: isTablet ? '0%' : '16%',
      mobile: false,
      sortable: true,
      sortComparator: sortByAgeGroup,
      inactiveSortIcon: SwapVertIcon,
    },
    {
      id: 'email',
      label: 'Email',
      align: 'left',
      width: isTablet ? '0%' : '28%',
      mobile: false,
      sortable: false,
    },
    {
      id: 'profile',
      label: 'Profile',
      align: 'left',
      width: isTablet ? '30%' : '16%',
      mobile: true,
      sortable: true,
      sortComparator: sortByProfileStatus,
      inactiveSortIcon: SwapVertIcon,
    },
    {
      id: 'actions',
      label: '',
      align: 'right',
      width: isTablet ? '25%' : '10%',
      mobile: true,
      sortable: false,
    },
  ]

  const pageCount =
    Math.ceil((filteredRepresentatives.length || 0) / rowsPerPage) || 1

  const rows = filteredRepresentatives.map(rep => (
    <AccountStudentRow
      key={rep.student.id}
      student={rep.student}
      relationshipType={rep.relationshipType}
    />
  ))

  return (
    <TableContainer className={classes.containerStyles}>
      <Box className={classes.tableShell}>
        <Box className={classes.toolbar}>
          <Box className={classes.searchGroup}>
            <SearchBar
              className={classes.searchField}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              placeholder="Search Accounts"
              data-testid="account-list-search"
              style={{ margin: 0 }}
            />
            {filterButtons}
          </Box>
          <Box className={classes.toolbarActions}>
            {filteredRepresentatives.length > 0 && (
              <StudentsTablePagination
                page={page}
                pageCount={pageCount}
                rowsPerPage={rowsPerPage}
                totalCount={filteredRepresentatives.length}
                onPageChange={setPage}
              />
            )}
          </Box>
        </Box>
        <Box className={classes.tableContent}>
          <EnhancedTable
            rows={rows}
            columns={columns}
            label="Accounts"
            numberOfRows={rowsPerPage}
            variant="list"
            rowHeight="56px"
            initialOrderBy="name"
            initialOrder="asc"
            page={page}
            onPageChange={setPage}
            showHeaderPagination={false}
            showPaginator={false}
            emptyState={() => (
              <Box className={classes.listEmptyStateContent}>
                <SectionEmptyState
                  Icon={PermContactCalendarOutlinedIcon}
                  title="Accounts"
                  caption="No matching accounts."
                  bordered={false}
                />
              </Box>
            )}
          />
        </Box>
      </Box>
    </TableContainer>
  )
}

export default AccountsTable
